<template>
  <div
    v-if="modelValue"
    ref="rootRef"
    class="context-iq-overlay"
    role="dialog"
    aria-modal="true"
    tabindex="-1"
    :aria-label="headerTitle"
    data-testid="context-iq-overlay"
    @keydown.esc.stop="handleClose"
  >
    <div class="context-iq-overlay__shell">
      <IQControlledOverlayHeader :title="headerTitle" @close="handleClose" />

      <div class="context-iq-overlay__body">
        <section class="context-iq-overlay__chat" aria-label="IQ conversation">
          <div
            ref="threadRef"
            class="context-iq-overlay__thread"
            aria-live="polite"
            data-testid="context-iq-thread"
          >
            <div
              v-for="message in messages"
              :key="message.id"
              class="context-iq-overlay__message"
              :class="`context-iq-overlay__message--${message.kind}`"
            >
              <IQUserPrompt
                v-if="message.kind === 'user'"
                :message="message.text"
                variant="light"
              />
              <IQAIResponse
                v-else
                :response="message.text"
                format="markdown"
                :is-thinking="false"
                :current-reasoning="''"
                surface="light"
              />
            </div>

            <IQThinkingBanner
              v-if="streaming && !hasStreamedText"
              data-testid="context-iq-thinking"
              state="thinking"
              :title="thinkingTitle"
              :phase="phase"
              :reasoning="reasoning"
              :stalled="stalled"
              :hide-retry="true"
              :use-store-reasoning="false"
            />

            <div v-if="errorMessage" class="context-iq-overlay__error" role="alert">
              <p class="context-iq-overlay__error-text" data-testid="context-iq-error">
                {{ errorMessage }}
              </p>
              <StandardButton
                label="Try again"
                variant="outline"
                size="small"
                data-testid="context-iq-retry"
                @click="retryLastTurn"
              />
            </div>
          </div>

          <div class="context-iq-overlay__prompt">
            <AskAQuestionPrompt
              :placeholder="promptPlaceholder"
              :max-attachments="0"
              :generating="streaming"
              @submit="handleSubmit"
              @stop="handleStop"
            />
          </div>
        </section>

        <aside class="context-iq-overlay__artifact" aria-label="Context editor">
          <div class="context-iq-overlay__editor">
            <ContextEditor
              title="Context"
              :context="context"
              :draft="artifact"
              :max-length="maxLength"
              :suggesting="streaming"
              :show-actions="false"
              @input="handleArtifactInput"
            />
          </div>

          <div class="context-iq-overlay__footer">
            <StandardButton
              label="Save Changes"
              size="small"
              :disabled="!canSave"
              data-testid="context-iq-save"
              @click="handleSave"
            />
            <StandardButton
              label="Discard"
              variant="outline"
              size="small"
              data-testid="context-iq-discard"
              @click="handleClose"
            />
          </div>
        </aside>
      </div>
    </div>

    <DiscardProposedChangesDialog
      :value="showDiscardDialog"
      :z-index="11000"
      @input="showDiscardDialog = $event"
      @onAction="confirmDiscard"
    />
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, ref, watch } from "vue";
import IQControlledOverlayHeader from "@components/IQControlledOverlayHeader/IQControlledOverlayHeader.vue";
import IQUserPrompt from "@components/IQUserPrompt/IQUserPrompt.vue";
import IQAIResponse from "@components/IQAIResponse/IQAIResponse.vue";
import IQThinkingBanner from "@components/IQThinkingBanner/IQThinkingBanner.vue";
import AskAQuestionPrompt from "@components/AskAQuestionPrompt/AskAQuestionPrompt.vue";
import ContextEditor from "@components/ContextEditor/ContextEditor.vue";
import StandardButton from "@components/StandardButton/StandardButton.vue";
import DiscardProposedChangesDialog from "@components/DiscardProposedChangesDialog/DiscardProposedChangesDialog.vue";
import { ENTITY_CONTEXT_MAX_LENGTH } from "@components/utils/useContextSuggestion";
import {
  CONTEXT_OBJECT_TYPES,
  suggestContextEntityStream,
} from "@/api/suggestContextEntityApi";

/**
 * ER-10140 — IQ Overlay (Mode A) for the Context field of a Responsibility, Role,
 * Position or Department. The left pane is a chat with IQ; the right pane holds
 * the ContextEditor as the artifact IQ writes into. Opening on an empty context
 * runs "Suggest"; opening on a saved context runs "Improve". Save hands the text
 * to the host (which writes it through the entity's update-context API).
 */

const IMPROVE_PROMPT =
  "Improve this context: make it clearer and more specific, keeping its meaning.";
const FALLBACK_ERROR = "IQ could not write a context right now. Please try again.";
const EMPTY_ERROR = "IQ did not return a context. Please try again.";

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
  objectType: {
    type: String,
    required: true,
    validator: (value) => CONTEXT_OBJECT_TYPES.includes(value),
  },
  objectId: {
    type: [String, Number],
    default: null,
  },
  objectName: {
    type: String,
    default: "",
  },
  // The saved context. Non-empty opens the overlay in "Improve" mode.
  context: {
    type: String,
    default: "",
  },
  maxLength: {
    type: Number,
    default: ENTITY_CONTEXT_MAX_LENGTH,
  },
});

const emit = defineEmits(["save", "close"]);

const rootRef = ref(null);
const threadRef = ref(null);
const messages = ref([]);
const artifact = ref("");
const streaming = ref(false);
const hasStreamedText = ref(false);
const errorMessage = ref("");
const phase = ref("");
const reasoning = ref("");
const stalled = ref(false);
const mode = ref("suggest");
const showDiscardDialog = ref(false);

let messageSeq = 0;
let turnGeneration = 0;
let abortController = null;
let lastTurn = null;
// Chat turns as the department op expects them ({role, content}).
let turns = [];

const isImprove = computed(() => mode.value === "improve");

const headerTitle = computed(() => {
  const action = isImprove.value ? "Improve" : "Suggest";
  const name = props.objectName ? ` “${props.objectName}”` : "";
  return `IQ ${action} Context for ${props.objectType}${name}`;
});

const thinkingTitle = computed(() =>
  isImprove.value ? "IQ is improving the context ..." : "IQ is writing the context ..."
);

const promptPlaceholder = "Tell IQ how to change the context, e.g. make it shorter";

const trimmedArtifact = computed(() => artifact.value.trim());

const isDirty = computed(() => trimmedArtifact.value !== (props.context || "").trim());

const canSave = computed(
  () =>
    !streaming.value &&
    !!trimmedArtifact.value &&
    artifact.value.length <= props.maxLength &&
    isDirty.value
);

watch(
  () => props.modelValue,
  (open) => {
    if (open) {
      startSession();
    } else {
      resetSession();
    }
  },
  { immediate: true }
);

onBeforeUnmount(() => {
  resetSession();
});

function nextId() {
  messageSeq += 1;
  return `context-iq-${messageSeq}`;
}

async function scrollThread() {
  await nextTick();
  const el = threadRef.value;
  if (el) el.scrollTop = el.scrollHeight;
}

function pushMessage(kind, text) {
  messages.value.push({ id: nextId(), kind, text });
  scrollThread();
}

function abortStream() {
  if (abortController) {
    abortController.abort();
    abortController = null;
  }
}

function resetSession() {
  turnGeneration += 1;
  abortStream();
  messages.value = [];
  artifact.value = "";
  streaming.value = false;
  hasStreamedText.value = false;
  errorMessage.value = "";
  phase.value = "";
  reasoning.value = "";
  stalled.value = false;
  showDiscardDialog.value = false;
  messageSeq = 0;
  lastTurn = null;
  turns = [];
}

function startSession() {
  resetSession();
  const saved = (props.context || "").trim();
  mode.value = saved ? "improve" : "suggest";
  artifact.value = saved;
  nextTick(() => rootRef.value?.focus());

  if (saved) {
    runTurn({ userText: "Improve this context", refinePrompt: IMPROVE_PROMPT });
  } else {
    runTurn({});
  }
}

function currentAccountId() {
  try {
    return JSON.parse(localStorage.getItem("currentAccountID"));
  } catch {
    return null;
  }
}

function doneMessage(followUp) {
  if (followUp) return "Updated. You can edit the context on the right or ask for more changes.";
  return isImprove.value
    ? "Here is an improved context. You can edit it on the right, ask for more changes, or save it."
    : "Here is a suggested context. You can edit it on the right, ask for changes, or save it.";
}

// userText: shown as the user's chat bubble (omitted on retry). followUp: a turn the
// operator typed, as opposed to the automatic seed / improve turn.
async function runTurn({ userText = "", refinePrompt = "", followUp = false }) {
  abortStream();
  const generation = ++turnGeneration;
  const controller = new AbortController();
  abortController = controller;
  lastTurn = { refinePrompt, followUp };

  const previousArtifact = artifact.value;
  const isRefine = !!refinePrompt;
  if (userText) pushMessage("user", userText);
  if (isRefine) turns.push({ role: "user", content: refinePrompt });

  streaming.value = true;
  hasStreamedText.value = false;
  errorMessage.value = "";
  phase.value = "";
  reasoning.value = "";
  stalled.value = false;

  const isCurrent = () => generation === turnGeneration;

  try {
    const finalText = await suggestContextEntityStream({
      objectType: props.objectType,
      accountId: currentAccountId(),
      objectId: props.objectId,
      currentContext: isRefine ? previousArtifact : "",
      refinePrompt,
      messages: props.objectType === "department" && isRefine ? turns : undefined,
      signal: controller.signal,
      onChunk: (text) => {
        if (!isCurrent()) return;
        hasStreamedText.value = true;
        artifact.value = text;
      },
      onPhase: (label) => {
        if (isCurrent()) phase.value = label;
      },
      onThinking: (text) => {
        if (isCurrent()) reasoning.value = text;
      },
      onStalled: () => {
        if (isCurrent()) stalled.value = true;
      },
    });
    if (!isCurrent()) return;
    if (!finalText) {
      artifact.value = previousArtifact;
      if (isRefine) turns.pop();
      errorMessage.value = EMPTY_ERROR;
      return;
    }
    artifact.value = finalText;
    turns.push({ role: "assistant", content: finalText });
    pushMessage("ai", doneMessage(followUp));
  } catch (error) {
    if (!isCurrent()) return;
    if (isRefine) turns.pop();
    if (controller.signal.aborted) {
      pushMessage("ai", "Stopped. Anything IQ already wrote is kept on the right.");
      return;
    }
    artifact.value = previousArtifact;
    errorMessage.value = error?.message || FALLBACK_ERROR;
  } finally {
    if (isCurrent()) {
      streaming.value = false;
      abortController = null;
    }
  }
}

function handleSubmit(text) {
  const prompt = String(text || "").trim();
  if (!prompt || streaming.value) return;
  runTurn({ userText: prompt, refinePrompt: prompt, followUp: true });
}

function handleStop() {
  abortStream();
}

function retryLastTurn() {
  if (!lastTurn || streaming.value) return;
  runTurn({ ...lastTurn });
}

function handleArtifactInput(value) {
  artifact.value = value;
}

function handleSave() {
  if (!canSave.value) return;
  emit("save", trimmedArtifact.value);
  emit("close");
}

function handleClose() {
  if (isDirty.value && trimmedArtifact.value && !showDiscardDialog.value) {
    showDiscardDialog.value = true;
    return;
  }
  emit("close");
}

function confirmDiscard() {
  showDiscardDialog.value = false;
  emit("close");
}
</script>

<style scoped>
.context-iq-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  outline: none;
}

.context-iq-overlay__shell {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  min-height: 0;
}

.context-iq-overlay__body {
  display: flex;
  flex: 1;
  min-height: 0;
}

.context-iq-overlay__chat {
  display: flex;
  flex: 0 0 50%;
  flex-direction: column;
  width: 50%;
  min-width: 0;
  min-height: 0;
  padding: 0 30px;
  border-right: 1px solid #eaecf0;
}

.context-iq-overlay__thread {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 16px;
  min-height: 0;
  padding: 24px 0;
  overflow-y: auto;
}

.context-iq-overlay__message {
  display: flex;
  flex-direction: column;
}

.context-iq-overlay__message--user {
  align-items: flex-end;
}

.context-iq-overlay__message--ai {
  align-items: flex-start;
}

.context-iq-overlay__error {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
}

.context-iq-overlay__error-text {
  margin: 0;
  font-family: Inter, sans-serif;
  font-size: 14px;
  line-height: 20px;
  color: #b42318;
}

.context-iq-overlay__prompt {
  flex-shrink: 0;
  padding: 12px 0 24px;
}

.context-iq-overlay__artifact {
  display: flex;
  flex: 0 0 50%;
  flex-direction: column;
  width: 50%;
  min-width: 0;
  min-height: 0;
  background: #ffffff;
}

.context-iq-overlay__editor {
  flex: 1;
  min-height: 0;
  padding: 24px 30px;
  overflow-y: auto;
}

.context-iq-overlay__footer {
  display: flex;
  flex-shrink: 0;
  gap: 10px;
  padding: 16px 30px;
  border-top: 1px solid #eaecf0;
}
</style>
